import { tagBuildingFace } from "building-types/buildingGeomTag/buildingGeomTags";
import type { BuildingGeomParentType, BuildingGeomTag } from "building-types/buildingGeomTag/buildingGeomTagTypes";
import {
  TAG_POSITION_BOTTOM,
  TAG_POSITION_END,
  TAG_POSITION_LEFT,
  TAG_POSITION_RIGHT,
  TAG_POSITION_START,
  TAG_POSITION_TOP,
} from "building-types/buildingGeomTag/buildingGeomTagTypes";
import { FinishClipping } from "building-types/casing/casingTypes";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { OpeningSideHolePlacement } from "building-types/openings/opening";
import type { OpeningSide } from "building-types/openings/openingTypes";
import { RoomKind } from "building-types/rooms/roomTypesEnum";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { BuildingGenerationIssueType } from "building-types/settings/buildingGenerationIssue";
import type { ExteriorWindowCasingSettings } from "building-types/settings/exteriorWindowCasingSettings";
import {
  ApronCasingType,
  HeadCasingType,
  SideCasingType,
  StoolCasingType,
} from "building-types/settings/settingsTypesEnum";
import type { WallAssembly } from "building-types/walls/wallAssemblyTypes";
import type { WallCut } from "building-types/walls/wallCutTypes";
import type { WallSection } from "building-types/walls/wallDesciptorTypes";
import type { WallEndCap } from "building-types/walls/wallEndsTypes";
import { WallType } from "building-types/walls/wallEnums";
import { WallLayerType } from "building-types/walls/wallEnums";
import type { StartSortedWallGaps, WallGap } from "building-types/walls/wallGap";
import { WallGapSource } from "building-types/walls/wallGap";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import type { SolidWallSide } from "building-types/walls/wallTypes";
import type {
  HalfEdgeWallEnd,
  SolidWall,
  SolidWallMass,
  WallLayer,
  WallMassBase,
  WallPath,
} from "building-types/walls/wallTypes";
import { SolidWallKind, WallLOD } from "building-types/walls/wallTypes";
import type { MergedWindowSettings, GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import type { Table } from "common/data/table";
import uid from "common/utils/uid";
import { WINDOW_INTERIOR_FRAME_WIDTH } from "doc/elements/window/constants";
import { defaultExteriorWindowCasingSettings } from "doc/settings/defaultSettings";
import { dynamicBuildingEntityBaseNew } from "doc/utils/buildingEntityTools";
import { wallGapNew } from "doc/utils/wallGapTools";
import type { Face } from "geom/brep";
import { faceNew, faceReverse, faceCopy, faceMove } from "geom/brep";
import { closestParamNormalizedLine2 } from "geom/closestPoint";
import { DIST_EPSILON } from "geom/core";
import { extrudeLine2 } from "geom/extrude";
import { line2ProjectPt } from "geom/line2";
import { offsetPolyLine2 } from "geom/offset";
import { planeCopy, planeNewXY } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import {
  polyLine2Centroid,
  polyLine2Copy,
  polyLine2EdgeEval,
  polyLine2EdgeLength,
  polyLine2Length,
  polyLine2ReverseCopy,
  polyLine2SetCW,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { vec2Dist, vec2Dot, vec2Lerp } from "geom/vec2";
import { isCombinedGlobalInstanceMirror } from "../../generationTools/generateOpeningsTools";
import { getFinalDoorPosition } from "../doors/doorUtils";
import { doorOpeningNew } from "../openings/doorOpening";
import { settingsClipFinish } from "../openings/openingUtils";
import { wallCutOpeningNew } from "../openings/wallCutOpening";
import { windowOpeningNew } from "../openings/windowOpenings";
import { makeWallCutOutline } from "../wallCuts";
import { windowRectangularOpening } from "../window/windowElevation";
import { getWindowHeight } from "../window/windowHeightTools";
import { windowHasCasing } from "../window/windowQuery";
import { mergeWallGaps } from "./solidWallCrossSectionUtils";
import { makeSolidWallFaces } from "./solidWallFaces";
import { getBackSide } from "./wallCut";
import { wallPathOpposite } from "./wallDescriptor";
import { solidWallLayerWithFallback } from "./wallLayers";
import { wallSideNew, wallSidePathDomainFromEdge } from "./wallSideBasics";

const SOLID_MASS_FROM_WALL_ENDS: BuildingGeomTag = {
  type: "CONDITION",
  condition: "SOLID_MASS_FROM_WALL_ENDS",
};

export function solidWallMassFromWallEnds(
  id: string,
  type: BuildingGeomParentType,
  path: WallPath,
  assembly: WallAssembly,
  floorElevation: number,
  start: WallEndCap,
  end: WallEndCap,
  section: WallSection,
  layers: WallLayer[] = [],
): SolidWallMass {
  const { bottomOffset, topOffset } = section;
  const { leftOffset, rightOffset } = start;

  const { verts: startCap, isFreeEnd: startIsFreeEnd } = start;
  const { verts: endCap, isFreeEnd: endIsFreeEnd } = end;

  const thickness = leftOffset + rightOffset;
  const height = topOffset - bottomOffset;

  // one shared tag per mass instead of one per face; tags are immutable
  const parentTag: BuildingGeomTag = { type: "PARENT", parentId: id, parentType: type };

  // leaving in here for reference - this is the original footpint
  // const footprint = polyLine2New([...startCap, ...endCap, startCap[0], startCap[1]]); was a big performance bottleneck
  const footprint = polyLine2New(startCap.length + endCap.length + 2);
  let i = 0;
  for (let l = startCap.length; i < l; i++) {
    footprint[i] = startCap[i];
  }
  for (let j = 0, l = endCap.length; j < l; j++) {
    footprint[i + j] = endCap[j];
  }
  footprint[footprint.length - 2] = startCap[0];
  footprint[footprint.length - 1] = startCap[1];

  const plane = planeNewXY(0, 0, floorElevation + bottomOffset);

  const leftEdge = polyLine2New(4);
  leftEdge[0] = endCap[endCap.length - 2];
  leftEdge[1] = endCap[endCap.length - 1];
  leftEdge[2] = startCap[0];
  leftEdge[3] = startCap[1];
  const leftFace = tagBuildingFace(massSideFace(leftEdge, plane, height), [
    parentTag,
    SOLID_MASS_FROM_WALL_ENDS,
    TAG_POSITION_LEFT,
  ]);

  const rightEdge = polyLine2New(4);
  rightEdge[0] = startCap[startCap.length - 2];
  rightEdge[1] = startCap[startCap.length - 1];
  rightEdge[2] = endCap[0];
  rightEdge[3] = endCap[1];
  const rightFace = tagBuildingFace(massSideFace(rightEdge, plane, height), [
    parentTag,
    SOLID_MASS_FROM_WALL_ENDS,
    TAG_POSITION_RIGHT,
  ]);

  // useful for debugging wall faces
  // rightFace.loops[0] = offsetPolyLine2(rightFace.loops[0], 0.25);
  // leftFace.loops[0] = offsetPolyLine2(leftFace.loops[0], 0.25);

  const leftDomain = wallSidePathDomainFromEdge(wallPathOpposite(path), leftEdge);
  const rightDomain = wallSidePathDomainFromEdge(path, rightEdge);

  // end faces

  let startFace: Face | null = null;
  let endFace: Face | null = null;

  if (startIsFreeEnd) {
    startFace = extrudeLine2(
      plane,
      startCap[0],
      startCap[1],
      startCap[startCap.length - 2],
      startCap[startCap.length - 1],
      0,
      0,
      height,
    );
  } else if (start.offsetDifferenceOutline) {
    const { offsetDifferenceOutline } = start;
    startFace = extrudeLine2(
      plane,
      offsetDifferenceOutline[0],
      offsetDifferenceOutline[1],
      offsetDifferenceOutline[2],
      offsetDifferenceOutline[3],
      0,
      0,
      height,
    );
  }

  if (endIsFreeEnd) {
    endFace = extrudeLine2(
      plane,
      endCap[0],
      endCap[1],
      endCap[endCap.length - 2],
      endCap[endCap.length - 1],
      0,
      0,
      height,
    );
  } else if (end.offsetDifferenceOutline) {
    const { offsetDifferenceOutline } = end;
    endFace = extrudeLine2(
      plane,
      offsetDifferenceOutline[0],
      offsetDifferenceOutline[1],
      offsetDifferenceOutline[2],
      offsetDifferenceOutline[3],
      0,
      0,
      height,
    );
  }

  if (!!startFace) {
    tagBuildingFace(startFace, [parentTag, SOLID_MASS_FROM_WALL_ENDS, TAG_POSITION_START]);
  }

  if (!!endFace) {
    tagBuildingFace(endFace, [parentTag, SOLID_MASS_FROM_WALL_ENDS, TAG_POSITION_END]);
  }

  const bottomFace = tagBuildingFace(
    faceReverse(faceNew([polyLine2SetCW(polyLine2Copy(footprint))], planeCopy(plane))),
    [parentTag, SOLID_MASS_FROM_WALL_ENDS, TAG_POSITION_BOTTOM],
  );

  const topFace = tagBuildingFace(faceMove(faceReverse(faceCopy(bottomFace)), 0, 0, height), [
    parentTag,
    SOLID_MASS_FROM_WALL_ENDS,
    TAG_POSITION_TOP,
  ]);

  const solidMass: Omit<SolidWallMass, "integerId" | "pickHandle" | "docEntityHandle" | "entityType" | "id"> = {
    wallType: WallType.SOLID_MASS,
    wallAssembly: assembly,
    path,
    start,
    end,
    section,
    floorElevation,
    thickness,
    leftOffset,
    leftDomain,
    rightOffset,
    rightDomain,
    footprint,
    bottomFace,
    topFace,
    leftFace,
    rightFace,
    leftEdge,
    rightEdge,
    startFace,
    endFace,
    layers,
  };

  const instanceId = path.leftRoom?.instanceId ?? path.rightRoom?.instanceId;

  return {
    ...dynamicBuildingEntityBaseNew("WALL", id),
    ...(instanceId
      ? {
          pickHandle: { type: "COMPONENT_INSTANCE", id: instanceId },
        }
      : {}),
    ...solidMass,
  };
}

function massSideFace(edge: PolyLine2, plane: Plane, height: number): Face {
  if (polyLine2Length(edge) > DIST_EPSILON) {
    const face = extrudeLine2(plane, edge[0], edge[1], edge[2], edge[3], 0, 0, height);
    if (face !== null) {
      return face;
    }
  }

  // fallback to creating a face with no area
  return faceNew([polyLine2New([0, 0, 0, 0, 0, 0, 0, 0])], planeNewXY());
}

interface SolidWallWithOpeningsOptions {
  id?: string;
  levelId: string;
  mass: SolidWallMass;
  path: WallPath;
  leftSide: SolidWallSide;
  rightSide: SolidWallSide;
  wallMassSystem: WallMassSystem;
  mirror: boolean;
  issues: BuildingGenerationIssue[];
}

export function solidWallWithOpenings({
  id,
  path,
  mass,
  leftSide,
  rightSide,
  wallMassSystem,
  mirror,
  issues,
}: SolidWallWithOpeningsOptions) {
  const framingMass = solidWallLayerWithFallback(mass, WallLayerType.STRUCTURE, WallLOD.L1);
  const substrateMass = solidWallLayerWithFallback(mass, WallLayerType.SUBSTRATE, WallLOD.L1);

  const { leftRoomKind, rightRoomKind, wallCondition } = path;

  const leftSidePlacement: OpeningSideHolePlacement = {
    edge: framingMass.leftEdge,
    edgeLength: polyLine2EdgeLength(framingMass.leftEdge, 0),
    edgePlane: framingMass.leftFace.plane,
    openingPlane: substrateMass.leftFace.plane,
    room: path.leftRoom,
    roomKind: leftRoomKind,
  };

  const rightSidePlacement: OpeningSideHolePlacement = {
    edge: framingMass.rightEdge,
    edgeLength: polyLine2EdgeLength(framingMass.rightEdge, 0),
    edgePlane: framingMass.rightFace.plane,
    openingPlane: substrateMass.rightFace.plane,
    room: path.rightRoom,
    roomKind: rightRoomKind,
  };

  const leftSideWithOpenings = solidWallSideWithOpenings({
    side: leftSide,
    frontPlacement: leftSidePlacement,
    backPlacement: rightSidePlacement,
    wallMassSystem,
    isLeftSide: true,
    mirror,
    issues,
  });

  const rightSideWithOpenings = solidWallSideWithOpenings({
    side: rightSide,
    frontPlacement: rightSidePlacement,
    backPlacement: leftSidePlacement,
    wallMassSystem,
    isLeftSide: false,
    mirror,
    issues,
  });

  const { wallFaces, wallOutlineFaces } = makeSolidWallFaces({
    mass,
    leftSide: leftSideWithOpenings,
    rightSide: rightSideWithOpenings,
  });
  const wallKind = determineWallKind(rightRoomKind, leftRoomKind);

  const doors = [...leftSideWithOpenings.doors, ...rightSideWithOpenings.doors];
  const windows = [...leftSideWithOpenings.windows, ...rightSideWithOpenings.windows];

  const wall: SolidWall = {
    id: id === undefined ? uid() : id,
    entityType: "WALL",
    pickHandle: undefined,
    docEntityHandle: undefined,
    wallType: WallType.SOLID,
    wallKind,
    mass,
    leftSide,
    rightSide,
    doorPlacements: doors,
    windowPlacements: windows,
    wallFaces,
    wallOutlineFaces,
    wallCondition,
    trimmedByStairs: false,
  };

  return wall;
}

export function determineWallKind(rightRoomKind: RoomKind, leftRoomKind: RoomKind): SolidWallKind {
  // handles null values as exterior
  const isRightRoomEffectivelyExterior = rightRoomKind === RoomKind.EXTERIOR || rightRoomKind === RoomKind.BOUNDARY;
  const isLeftRoomEffectivelyExterior = leftRoomKind === RoomKind.EXTERIOR || leftRoomKind === RoomKind.BOUNDARY;

  if (isRightRoomEffectivelyExterior && isLeftRoomEffectivelyExterior) {
    return SolidWallKind.NONENCLOSURE;
  }
  if (isRightRoomEffectivelyExterior !== isLeftRoomEffectivelyExterior) {
    return SolidWallKind.EXTERIOR;
  }
  return SolidWallKind.INTERIOR;
}

function solidWallSideWithOpenings({
  side,
  frontPlacement,
  backPlacement,
  wallMassSystem,
  mirror,
  issues,
}: {
  side: SolidWallSide;
  frontPlacement: OpeningSideHolePlacement;
  backPlacement: OpeningSideHolePlacement;
  wallMassSystem: WallMassSystem;
  isLeftSide: boolean;
  mirror: boolean;
  issues: BuildingGenerationIssue[];
}) {
  const { windows, doors, wallCuts, finishes } = side;

  const wallSideWithOpenings = wallSideNew({
    finishes,
    descriptor: side.descriptor,
  });

  for (const window of windows) {
    if (
      solidWallTryAddWindowOpening({
        window,
        frontPlacement,
        backPlacement,
        wallMassSystem,
        mirror: isCombinedGlobalInstanceMirror(mirror, window.frontRoom ?? window.backRoom),
        issues,
      })
    ) {
      wallSideWithOpenings.windows.push(window);
    }
  }
  for (const door of doors) {
    if (
      solidWallTryAddDoorOpening({
        door,
        frontPlacement,
        backPlacement,
        wallMassSystem,
        mirror: isCombinedGlobalInstanceMirror(mirror, door.frontRoom ?? door.backRoom),
        issues,
      })
    ) {
      wallSideWithOpenings.doors.push(door);
    }
  }
  for (const wallCut of wallCuts) {
    if (
      solidWallTryAddWallCutOpening({
        wallCut,
        frontPlacement,
        backPlacement,
      })
    ) {
      wallSideWithOpenings.wallCuts.push(wallCut);
    }
  }

  return wallSideWithOpenings;
}

interface SolidWallTryAddWindowOpeningOptions {
  window: GeneratedWindowPlacement;
  frontPlacement: OpeningSideHolePlacement;
  backPlacement: OpeningSideHolePlacement;
  wallMassSystem: WallMassSystem;
  mirror: boolean;
  issues: BuildingGenerationIssue[];
}

export function solidWallTryAddWindowOpening({
  window,
  frontPlacement: placement,
  backPlacement: placementOpposite,
  wallMassSystem,
  mirror,
  issues,
}: SolidWallTryAddWindowOpeningOptions): boolean {
  const edge = placement.edge;
  const edgeLen = placement.edgeLength;
  const profileRectangular = windowRectangularOpening(window, edge, edgeLen, wallMassSystem);
  if (!profileRectangular) {
    if (window.handle) {
      issues.push({
        type: BuildingGenerationIssueType.WINDOW_OPENING_EXCEEDS_BOUNDARY,
        handle: window.handle,
      });
    }
    return false;
  }

  const edgeOpposite = placementOpposite.edge;
  const profileRectangularOpposite: OpeningSide | null = getBackSide({
    hole: profileRectangular,
    frontEdge: edge,
    backEdge: edgeOpposite,
    ignoreLimits: true,
  });

  /** It is rare that a WINDOW_V2 would be caught here as the placement algorithm in
   * GenerateOpenings.ts is much more robust than WINDOW. Go there to see the issues
   * for placement.
   */
  if (!profileRectangularOpposite) {
    if (window.handle) {
      issues.push({
        type: BuildingGenerationIssueType.WINDOW_OPENING_EXCEEDS_BOUNDARY,
        handle: window.handle,
      });
    }
    return false;
  }

  const settings = {
    exteriorCasing: window.exteriorCasing,
    interiorCasing: window.interiorCasing,
  } as MergedWindowSettings;

  window.opening = windowOpeningNew(
    window,
    settings,
    placement,
    profileRectangular,
    placementOpposite,
    profileRectangularOpposite,
    mirror,
  );

  return true;
}

interface SolidWallTryAddWallCutOpeningOptions {
  wallCut: WallCut;
  frontPlacement: OpeningSideHolePlacement;
  backPlacement: OpeningSideHolePlacement;
}

function solidWallTryAddWallCutOpening({
  wallCut,
  frontPlacement,
  backPlacement,
}: SolidWallTryAddWallCutOpeningOptions): boolean {
  const frontEdge = frontPlacement.edge;
  const frontEdgeLen = frontPlacement.edgeLength;
  const frontHole = makeWallCutOutline(wallCut, frontEdgeLen, frontPlacement.openingPlane);
  if (!frontHole) {
    console.assert(false, "wall cut exceeds wall boundary");
    return false;
  }

  const backEdge = backPlacement.edge;

  const backHole = getBackSide({ hole: frontHole, frontEdge, backEdge, ignoreLimits: !!wallCut.edgeParamAbsolute });
  if (!backHole) {
    console.assert(false, "wall cut exceeds wall boundary");
    return false;
  }

  wallCut.opening = wallCutOpeningNew(wallCut, frontPlacement, frontHole, backPlacement, backHole);

  return true;
}

interface SolidWallTryAddDoorOpeningOptions {
  door: GeneratedDoorPlacement;
  frontPlacement: OpeningSideHolePlacement;
  backPlacement: OpeningSideHolePlacement;
  wallMassSystem: WallMassSystem;
  mirror: boolean;
  issues: BuildingGenerationIssue[];
}

export function solidWallTryAddDoorOpening({
  door,
  frontPlacement,
  backPlacement,
  wallMassSystem,
  mirror,
  issues,
}: SolidWallTryAddDoorOpeningOptions) {
  const frontEdge = frontPlacement.edge;
  const frontEdgeLen = frontPlacement.edgeLength;
  const frontProfileRectangular = getFinalDoorPosition(door, frontEdge, frontEdgeLen, wallMassSystem);

  if (!frontProfileRectangular) {
    if (door.handle) {
      issues.push({
        type: BuildingGenerationIssueType.DOOR_OPENING_EXCEEDS_BOUNDARY,
        handle: door.handle,
      });
    }

    return false;
  }

  const backEdge = backPlacement.edge;

  let backProfileRectangular: OpeningSide | null = null;
  switch (door.type) {
    case "DOOR": {
      backProfileRectangular = getBackSide({
        hole: frontProfileRectangular,
        frontEdge,
        backEdge,
        ignoreLimits: !!door.useStripLimits,
      });
      break;
    }
    case "DOOR_V2": {
      // set ignore limits to true because the DOOR_V2 handles limits in `generateOpenings.ts`.
      backProfileRectangular = getBackSide({
        hole: frontProfileRectangular,
        frontEdge,
        backEdge,
        ignoreLimits: true,
      });
      break;
    }
  }
  if (!backProfileRectangular) {
    /** It is rare that a DOOR_V2 would be caught here as the placement algorithm in
     * GenerateOpenings.ts is much more robust than DOOR. Go there to see the issues
     * for placement.
     */
    if (door.handle) {
      issues.push({
        type: BuildingGenerationIssueType.DOOR_OPENING_EXCEEDS_BOUNDARY,
        handle: door.handle,
      });
    }

    return false;
  }

  door.opening = doorOpeningNew(
    door,
    frontPlacement,
    frontProfileRectangular,
    backPlacement,
    backProfileRectangular,
    mirror,
  );

  return true;
}

export function makeWallGap(
  opening: (GeneratedWindowPlacement | GeneratedDoorPlacement | WallCut)[],
  edge: PolyLine2,
  planOutline: PolyLine2,
  bottom: number,
  height: number,
  normalized: boolean = true,
  gapPadding: number = 0,
  noMerge: boolean = false,
  gapSource?: WallGapSource,
  gapSourceId?: string,
): WallGap {
  // console.assert(pl.length === 4, "makeWallGap assumes the strip is straight");
  // console.assert(planOutline.length === 10, "makeWallGap assumes the planOutline is a closed rectangular polygon");

  const ax = edge[0];
  const ay = edge[1];

  const bx = edge[2];
  const by = edge[3];

  const len = vec2Dist(ax, ay, bx, by);
  const normalizedCasingWidth = gapPadding / len;

  const [startX, startY] = vec2Lerp(planOutline[2], planOutline[3], planOutline[4], planOutline[5], 0.5);
  const [centerX, centerY] = polyLine2Centroid(planOutline) ?? [];
  const [endX, endY] = vec2Lerp(planOutline[6], planOutline[7], planOutline[8], planOutline[9], 0.5);

  let startT = closestParamNormalizedLine2(ax, ay, bx, by, startX, startY);
  let centerT = closestParamNormalizedLine2(ax, ay, bx, by, centerX, centerY);
  let endT = closestParamNormalizedLine2(ax, ay, bx, by, endX, endY);

  if (vec2Dot(bx - ax, by - ay, endX - startX, endY - startY) < 0.0) {
    const tmp = startT;
    startT = endT;
    endT = tmp;
  }

  startT = Math.max(startT - normalizedCasingWidth, 0);
  endT = Math.min(endT + normalizedCasingWidth, 1);

  if (!normalized) {
    startT *= len;
    centerT *= len;
    endT *= len;
  }

  const hasCasing = gapPadding > DIST_EPSILON;

  return wallGapNew(
    opening,
    startT,
    centerT,
    endT,
    bottom,
    height,
    hasCasing,
    hasCasing,
    noMerge,
    gapSource,
    gapSourceId,
  );
}

export function getWindowInteriorCasingOffsets() {
  return {
    sideOffset: WINDOW_INTERIOR_FRAME_WIDTH,
    topOffset: WINDOW_INTERIOR_FRAME_WIDTH,
    bottomOffset: WINDOW_INTERIOR_FRAME_WIDTH,
  };
}

export function getWindowExteriorCasingOffsets({
  window,
  includeApronCasing,
  defaultWindowFrameWidth = 0,
  includeExteriorCasing, // DO NOT set default value: undefined is meaningful
}: {
  window: Partial<GeneratedWindowPlacement> &
    Pick<GeneratedWindowPlacement, "descriptor" | "exteriorCasing" | "streetOrientation">;
  includeApronCasing: boolean | undefined;
  defaultWindowFrameWidth?: number;
  /**
   * if true, exterior casing will *always* be included, regardless of the window's clipping settings
   * if false, exterior casing will *never* be included, regardless of the window's clipping settings
   * if undefined, exterior casing will be included if the window's settings allow it
   */
  includeExteriorCasing?: boolean;
}): { sideOffset: number; topOffset: number; bottomOffset: number } {
  let sideOffset = defaultWindowFrameWidth;
  let topOffset = defaultWindowFrameWidth;
  let bottomOffset = defaultWindowFrameWidth;

  const settings = (window?.exteriorCasing ?? defaultExteriorWindowCasingSettings) as ExteriorWindowCasingSettings;
  const { apronCasingType, sideCasingWidth = 0, apronHeight = 0, stoolCasingThickness = 0 } = settings;

  if (!windowHasCasing(window, settings)) {
    return { sideOffset, topOffset, bottomOffset };
  }

  if (includeExteriorCasing || (includeExteriorCasing !== false && settings && settingsClipFinish(settings))) {
    if (
      (includeExteriorCasing || settings.sideCasingFinishClipping !== FinishClipping.NO_CLIPPING) &&
      settings.sideCasingType !== SideCasingType.DISABLED
    ) {
      sideOffset = sideCasingWidth;
    }

    if (
      includeApronCasing &&
      (includeExteriorCasing || settings.apronCasingFinishClipping !== FinishClipping.NO_CLIPPING) &&
      apronCasingType !== ApronCasingType.DISABLED
    ) {
      bottomOffset = apronHeight;
    }

    if (
      (includeExteriorCasing || settings.stoolCasingFinishClipping !== FinishClipping.NO_CLIPPING) &&
      settings.stoolCasingType !== StoolCasingType.DISABLED
    ) {
      bottomOffset = stoolCasingThickness;
    }

    if (
      (includeExteriorCasing || settings.headCasingFinishClipping !== FinishClipping.NO_CLIPPING) &&
      settings.headCasingType !== HeadCasingType.NONE &&
      settings.headCasingType !== HeadCasingType.SLICES &&
      settings.headCasingType !== HeadCasingType.ARCHED_SLICES
    ) {
      topOffset = settings.headCasingHeight;
    }
  }

  return { sideOffset, topOffset, bottomOffset };
}

export function gableEndWindowGaps(
  edge: PolyLine2,
  edgeWindows: Table<GeneratedWindowPlacement>,
  normalized: boolean = false,
): WallGap[] {
  const params: WallGap[] = [];

  const edgeScale = normalized ? 1 / polyLine2EdgeLength(edge, 0) : 1;

  if (edgeWindows) {
    const scale = edgeScale;

    for (const windowId in edgeWindows) {
      const window = edgeWindows[windowId];
      const { opening, verticalOffset, descriptor } = window;
      if (!opening) {
        continue;
      }

      const winVertOffset = verticalOffset || 0;

      const hole = opening.front;
      params.push(
        wallGapNew(
          [window],
          hole.startParam * scale,
          hole.centerParam * scale,
          hole.endParam * scale,
          winVertOffset,
          getWindowHeight(descriptor),
          false,
          false,
          false,
          WallGapSource.WINDOW,
        ),
      );
    }
  }
  return params;
}

export function openTopRect(
  botLeftX: number,
  botLeftY: number,
  topLeftX: number,
  topLeftY: number,
  botRightX: number,
  botRightY: number,
  topRightX: number,
  topRightY: number,
) {
  const outline = polyLine2New(8);
  outline[0] = topLeftX;
  outline[1] = topLeftY;
  outline[2] = botLeftX;
  outline[3] = botLeftY;
  outline[4] = botRightX;
  outline[5] = botRightY;
  outline[6] = topRightX;
  outline[7] = topRightY;
  return outline;
}

export function openBottomRect(
  botLeftX: number,
  botLeftY: number,
  topLeftX: number,
  topLeftY: number,
  botRightX: number,
  botRightY: number,
  topRightX: number,
  topRightY: number,
) {
  const outline = polyLine2New(8);
  outline[0] = botRightX;
  outline[1] = botRightY;
  outline[2] = topRightX;
  outline[3] = topRightY;
  outline[4] = topLeftX;
  outline[5] = topLeftY;
  outline[6] = botLeftX;
  outline[7] = botLeftY;
  return outline;
}

export function rect(
  botLeftX: number,
  botLeftY: number,
  topLeftX: number,
  topLeftY: number,
  botRightX: number,
  botRightY: number,
  topRightX: number,
  topRightY: number,
) {
  const outline = polyLine2New(10);
  outline[0] = botLeftX;
  outline[1] = botLeftY;
  outline[2] = botRightX;
  outline[3] = botRightY;
  outline[4] = topRightX;
  outline[5] = topRightY;
  outline[6] = topLeftX;
  outline[7] = topLeftY;
  outline[8] = botLeftX;
  outline[9] = botLeftY;
  return outline;
}

export function wallMassBaseCrossSection(wall: WallMassBase, closedPolyLines: boolean = false): PolyLine2[] {
  const { leftEdge, rightEdge } = wall;

  const topRect = closedPolyLines ? rect : openTopRect;
  const bottomRect = closedPolyLines ? rect : openBottomRect;

  const botLeftX = leftEdge[2];
  const botLeftY = leftEdge[3];

  const topLeftX = leftEdge[0];
  const topLeftY = leftEdge[1];

  const botRightX = rightEdge[0];
  const botRightY = rightEdge[1];

  const topRightX = rightEdge[2];
  const topRightY = rightEdge[3];

  if (wall.start.isFreeEnd) {
    if (wall.end.isFreeEnd) {
      return [rect(botLeftX, botLeftY, topLeftX, topLeftY, botRightX, botRightY, topRightX, topRightY)];
    } else {
      return [topRect(botLeftX, botLeftY, topLeftX, topLeftY, botRightX, botRightY, topRightX, topRightY)];
    }
  } else {
    if (wall.end.isFreeEnd) {
      return [bottomRect(botLeftX, botLeftY, topLeftX, topLeftY, botRightX, botRightY, topRightX, topRightY)];
    }
  }

  if (closedPolyLines) {
    const firstHalf: number[] = [];
    const secondHalf: number[] = [];
    if (wall.start.offsetDifferenceOutline) {
      firstHalf.push(
        wall.start.offsetDifferenceOutline[0],
        wall.start.offsetDifferenceOutline[1],
        wall.start.offsetDifferenceOutline[2],
        wall.start.offsetDifferenceOutline[3],
        botRightX,
        botRightY,
        topRightX,
        topRightY,
      );

      secondHalf.push(topLeftX, topLeftY, wall.start.offsetDifferenceOutline[0], wall.start.offsetDifferenceOutline[1]);
    } else {
      firstHalf.push(botLeftX, botLeftY, botRightX, botRightY, topRightX, topRightY);
      secondHalf.push(topLeftX, topLeftY, botLeftX, botLeftY);
    }

    if (wall.end.offsetDifferenceOutline) {
      return [
        polyLine2New([
          ...firstHalf,
          wall.end.offsetDifferenceOutline[0],
          wall.end.offsetDifferenceOutline[1],
          wall.end.offsetDifferenceOutline[2],
          wall.end.offsetDifferenceOutline[3],
          ...secondHalf,
        ]),
      ];
    } else {
      return [polyLine2New([...firstHalf, ...secondHalf])];
    }
  } else {
    const edgePolys: PolyLine2[] = [];
    if (wall.start.offsetDifferenceOutline) {
      edgePolys.push(wall.start.offsetDifferenceOutline as any as PolyLine2);
    }

    edgePolys.push(polyLine2ReverseCopy(leftEdge));

    if (wall.end.offsetDifferenceOutline) {
      edgePolys.push(wall.end.offsetDifferenceOutline as any as PolyLine2);
    }

    edgePolys.push(polyLine2Copy(rightEdge));

    return edgePolys;
  }
}

function addCrossSectionPoints(
  leftPts: number[],
  rightPts: number[],
  leftEdge: PolyLine2,
  rightEdge: PolyLine2,
  mergedLeftGaps: WallGap[],
  mergedRightGaps: WallGap[],
) {
  for (let i = 0, li = Math.min(mergedLeftGaps.length, mergedRightGaps.length); i < li; i++) {
    let [leftX, leftY] = polyLine2EdgeEval(leftEdge, 0, mergedLeftGaps[mergedLeftGaps.length - i - 1].end);
    let [rightX, rightY] = polyLine2EdgeEval(rightEdge, 0, mergedRightGaps[i].start);

    leftPts.push(leftX, leftY);
    rightPts.push(rightX, rightY);

    [leftX, leftY] = polyLine2EdgeEval(leftEdge, 0, mergedLeftGaps[mergedLeftGaps.length - i - 1].start);
    [rightX, rightY] = polyLine2EdgeEval(rightEdge, 0, mergedRightGaps[i].end);

    leftPts.push(leftX, leftY);
    rightPts.push(rightX, rightY);
  }
}

function buildOutlines(leftPts: number[], rightPts: number[]) {
  const outlines: PolyLine2[] = [];

  for (let i = 0, li = leftPts.length; i < li; i += 4) {
    const [botLeftX, botLeftY, topLeftX, topLeftY] = leftPts.slice(i, i + 4);
    const [botRightX, botRightY, topRightX, topRightY] = rightPts.slice(i, i + 4);
    outlines.push(rect(botLeftX, botLeftY, topLeftX, topLeftY, botRightX, botRightY, topRightX, topRightY));
  }

  return outlines;
}

/*
  A simpler version of the logic in solidWallCrossSection for building outlines from a simple 
  pair of edges
*/
export function makeCrossSectionFromWallGaps(
  leftGaps: StartSortedWallGaps,
  rightGaps: StartSortedWallGaps,
): PolyLine2[] {
  const { edge: leftEdge, gaps: mergedLeftGaps } = mergeWallGaps(leftGaps);
  const { edge: rightEdge, gaps: mergedRightGaps } = mergeWallGaps(rightGaps);

  const leftPts = [leftEdge[leftEdge.length - 2], leftEdge[leftEdge.length - 1]];
  const rightPts = [rightEdge[0], rightEdge[1]];
  addCrossSectionPoints(leftPts, rightPts, leftEdge, rightEdge, mergedLeftGaps, mergedRightGaps);

  leftPts.push(leftEdge[0], leftEdge[1]);
  rightPts.push(rightEdge[rightEdge.length - 2], rightEdge[rightEdge.length - 1]);

  return buildOutlines(leftPts, rightPts);
}

/**
 *
 * SolidWallMass HalfWall is the wall end info from one side of the wall
 */
export function solidWallMassHalfWall(wallMass: SolidWallMass, isEnd: boolean): HalfEdgeWallEnd {
  const projectToPath = (edge: PolyLine2) => {
    const [prsx, prsy] = line2ProjectPt(path.startX, path.startY, path.endX, path.endY, edge[0], edge[1]);
    const [prex, prey] = line2ProjectPt(path.startX, path.startY, path.endX, path.endY, edge[2], edge[3]);
    return polyLine2New([prsx, prsy, prex, prey]);
  };

  const { start, end, rightEdge, leftEdge, path } = wallMass;

  const endCap = isEnd ? end : start;
  const { leftOffset, rightOffset } = endCap;

  const leftProjectedOffset = isEnd ? rightOffset : leftOffset;
  const rightProjectedOffset = isEnd ? leftOffset : rightOffset;

  let leftProjectedEdge = projectToPath(leftEdge);
  let rightProjectedEdge = projectToPath(rightEdge);

  leftProjectedEdge = offsetPolyLine2(leftProjectedEdge, leftProjectedOffset);
  rightProjectedEdge = offsetPolyLine2(rightProjectedEdge, rightProjectedOffset);

  if (isEnd) {
    const t = rightProjectedEdge;
    rightProjectedEdge = leftProjectedEdge;
    leftProjectedEdge = t;
  }

  return {
    ...endCap,
    leftEdge: leftProjectedEdge,
    rightEdge: rightProjectedEdge,
  };
}
